<template>
  <BbInfiniteScroll :handler="loadAssets" :distance="240" :disabled="disableInfiniteScroll" class="media-assets">
    <BbMediaCard
      v-for="image in imagesByTab[tabKey]"
      :assetId="image.documentNumber"
      :key="image.documentNumber || image.name"
      :id="image.documentNumber"
      :name="image.name"
      :url="image.url"
      status="approved"
      statusText="Released"
      :version="image.version || 1"
      :data-attr="tabKey === MEDIA_TYPE.userImages ? 'my-images-insert' : undefined"
      @click="selectImage(image.originalUrl)"
    />
    <template v-if="disableInfiniteScroll && hasMoreImages">
      <BbMediaCardSkeleton v-for="index in 4" size="large" :key="index" />
    </template>
  </BbInfiniteScroll>
</template>

<script>
import { useWesley } from '@anthill-modules/wesley-components';
import { useAuth0 } from '@auth0/auth0-vue';
import { getOrgId } from '~/utils/auth-claims';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { FILTER_TYPE, MEDIA_TYPE } from '~/constants/documents';

import useDocuments from '~/composables/useDocuments';
import useImageGeneration from '~/composables/useImageGeneration';
import useFormFilters from '~/composables/useFormFilters';
import useModals from '~/composables/useModals';
import useDocumentEditor from '~/composables/useDocumentEditor';
import { getDocumentKeyForUserImage } from '~/utils/editor-utils';

export default {
  props: {
    tabKey: {
      type: String,
      default: MEDIA_TYPE.imported,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const route = useRoute();
    const { mediaPanelOpened, setMediaPanelOpened } = useModals();
    const { sendMessage: sendMessageToDocument, asyncSendMessage: sendAsyncMessageToDocument } = useWesley();
    const { formFilters, searchQuery, setSearchQuery } = useFormFilters();
    const { user } = useAuth0();
    const { fetchMediaImages, getAuthoredDocumentThumbs, setTotalMediaImages, totalMediaImages } = useDocuments();
    const { retrieveImages, getUserImages } = useImageGeneration();
    const { saveHtmlDocumentContentToDb, renderCitationTooltipsInIframe, uploadAuthoredOriginalImage } =
      useDocumentEditor();

    const hasMoreImages = ref(false);
    const isLoading = ref(false);
    const offset = ref(0);
    const limit = ref(window.innerWidth > 2000 ? 25 : 20);
    const imagesByTab = ref({
      [MEDIA_TYPE.imported]: [],
      [MEDIA_TYPE.userImages]: [],
    });

    const totalMediaImportedEntries = computed(() => totalMediaImages.value);
    const disableInfiniteScroll = computed(() => isLoading.value || !hasMoreImages.value);

    const prepareDataForRequest = (type) => {
      const filters = transformFiltersByType(formFilters.value, type); // only current type
      const search = searchQuery.value || '';
      hasMoreImages.value = offset.value + limit.value < totalMediaImportedEntries.value || offset.value === 0;
      isLoading.value = true;

      return { filters, search };
    };

    const transformFiltersByType = (formFilters, type) => {
      const typeFilters = formFilters[type] || {};
      const result = [];

      for (const [key, values] of Object.entries(typeFilters)) {
        if (values && values.length > 0) {
          result.push({
            key,
            value: Array.isArray(values) ? values : [values],
          });
        }
      }

      return result;
    };

    const loadImportedImages = async (currentTab, tabImages) => {
      const { filters, search } = prepareDataForRequest(FILTER_TYPE.media);
      const orgId = getOrgId(user.value);

      const response = await fetchMediaImages({
        query: search,
        orgId,
        filter: filters,
        from: offset.value,
        size: limit.value,
      });

      // Ignore if tab changed
      if (currentTab !== props.tabKey) return;

      const hits = response.hits?.hits || [];
      setTotalMediaImages(response.hits?.total?.value);

      const imagesUrl = await getAuthoredDocumentThumbs(hits.map((image) => image.externalId));
      const newImages = hits.map((hit) => ({
        ...hit,
        url:
          imagesUrl.find((img) => img.id === hit.externalId && img.variant === 'medium')?.url ||
          imagesUrl.find((img) => img.id === hit.externalId)?.url ||
          '',
        originalUrl: imagesUrl.find((img) => img.id === hit.externalId)?.url || '',
      }));

      imagesByTab.value[currentTab] = [...tabImages, ...newImages];
      offset.value += limit.value;
      isLoading.value = false;
    };

    const loadUserImages = async (currentTab, tabImages) => {
      isLoading.value = true;

      const { images: userImages, total } = await retrieveImages({
        from: offset.value,
        size: limit.value,
      });

      if (currentTab !== props.tabKey) return;

      imagesByTab.value[currentTab] = [
        ...tabImages,
        ...userImages.map((item) => ({
          ...item,
          name: item.prompt,
          originalUrl: item.url,
        })),
      ];

      hasMoreImages.value = offset.value + limit.value < total || offset.value === 0;
      offset.value += limit.value;
      isLoading.value = false;
    };

    const loadAssets = async () => {
      const currentTab = props.tabKey;
      const tabImages = imagesByTab.value[currentTab] || [];

      if (currentTab === MEDIA_TYPE.imported) {
        await loadImportedImages(currentTab, tabImages);
      } else {
        await loadUserImages(currentTab, tabImages);
      }
    };

    const selectImage = async (img) => {
      const imageData = imagesByTab.value[props.tabKey].find((image) => image.originalUrl === img);
      let imageUrl = img;

      if (imageData?.externalId) {
        try {
          const freshImages = await getAuthoredDocumentThumbs([imageData.externalId]);
          const freshImage = freshImages.find(
            (image) => image.id === imageData.externalId && image.variant === 'original',
          );
          if (freshImage?.url) {
            imageUrl = freshImage.url;
          }
        } catch (error) {
          console.warn('Failed to refresh image URL, using original:', error);
        }
      } else {
        try {
          const freshImages = await getUserImages([imageData.id]);
          const freshImage = freshImages.find((image) => image.id === imageData.id && image.variant === 'original');
          if (freshImage?.url) {
            imageUrl = freshImage.url;
          }
          // we need to make a copy of the image from user images bucket to authoring bucket
          const newReadUrl = await uploadAuthoredOriginalImage({
            authDocumentId: route.params.conversationId,
            documentId: getDocumentKeyForUserImage(imageUrl),
            originalUserImageSrc: imageUrl,
          });

          if (newReadUrl) {
            imageUrl = newReadUrl;
          }
        } catch (error) {
          console.warn('Failed to refresh user image URL, using original:', error);
        }
      }

      await sendAsyncMessageToDocument({
        method: 'updateAttribute',
        params: {
          query: mediaPanelOpened.value.cssPath,
          value: imageUrl,
          attr: 'src',
        },
      });
      setMediaPanelOpened({ opened: false });
      setTimeout(() => {
        renderCitationTooltipsInIframe();
        setSearchQuery('');
        sendMessageToDocument({ method: 'deselectElement' });
        saveHtmlDocumentContentToDb();
      }, 1000);
    };

    const updateLimitOnResize = () => {
      limit.value = window.innerWidth > 2000 ? 25 : 20;
    };

    watch(
      () => [formFilters.value, searchQuery.value],
      async () => {
        offset.value = 0;
        imagesByTab.value[props.tabKey] = [];
        await loadAssets();
      },
      { deep: true, immediate: true },
    );

    watch(
      () => props.tabKey,
      async () => {
        offset.value = 0;
        hasMoreImages.value = false;
        imagesByTab.value = {
          [MEDIA_TYPE.imported]: [],
          [MEDIA_TYPE.userImages]: [],
        };
        await loadAssets();
        setSearchQuery('');
      },
      { deep: true, immediate: true },
    );

    onMounted(async () => {
      window.addEventListener('resize', updateLimitOnResize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateLimitOnResize);
    });

    return { t, loadAssets, selectImage, disableInfiniteScroll, imagesByTab, hasMoreImages, MEDIA_TYPE };
  },
};
</script>

<style lang="scss" scoped>
.media-assets {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  grid-gap: $bb-spacing-4 $bb-spacing-5;

  @media all and (min-width: 1440px) {
    grid-gap: $bb-spacing-4 $bb-spacing-5;
  }

  @media all and (min-width: 1600px) {
    grid-gap: $bb-spacing-5 $bb-spacing-7;
  }

  @media all and (min-width: 1920px) {
    grid-gap: $bb-spacing-5 $bb-spacing-9;
  }

  @media all and (min-width: 2560px) {
    grid-gap: $bb-spacing-7 $bb-spacing-11;
  }
}
</style>
